import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button as MuiButton,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import styled from "@emotion/styled";
import SecurityIcon from "@mui/icons-material/Security";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { disable, enable, getSetup, getStatus, verify } from "../../services/mfaApi";
import Notification from "../../components/Notification";

const Card = styled(MuiCard)(spacing);
const TextField = styled(MuiTextField)(spacing);
const Button = styled(MuiButton)(spacing);

const sanitizeCode = (value) => value.replace(/\D/g, "").slice(0, 6);
const isLikelyBase64 = (value) => /^[A-Za-z0-9+/=]+$/.test(value) && value.length > 50;

const MfaSettings = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState({ isEnabled: false, isConfigured: false });
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [qrImageUrl, setQrImageUrl] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [disableLoading, setDisableLoading] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: "", type: "success" });

  const showNotify = useCallback((type, message) => {
    setNotify({ isOpen: true, message, type });
  }, []);

  const getErrorMessage = useCallback((result) => {
    if (result.status === 401 || result.status === 403) {
      return t("Session expired");
    }

    if (result.status === 404) {
      return t("User not found");
    }

    if (result.status === 400) {
      return "Invalid code";
    }

    return t(result.message || "Something went wrong");
  }, [t]);

  const handleLoadStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError("");
    const result = await getStatus();

    if (!result.ok) {
      const errorMessage = getErrorMessage(result);
      setStatusError(errorMessage);
      showNotify("error", errorMessage);
      setStatusLoading(false);
      return;
    }

    setStatus(result.data);
    setStatusLoading(false);
  }, [getErrorMessage, showNotify]);

  useEffect(() => {
    handleLoadStatus();
  }, [handleLoadStatus]);

  const handleSetup = async () => {
    setSetupLoading(true);
    setNotify((prev) => ({ ...prev, isOpen: false }));
    const result = await getSetup();

    if (!result.ok) {
      showNotify("error", getErrorMessage(result));
      setSetupLoading(false);
      return;
    }

    setSetupData(result.data);
    setSetupLoading(false);
  };

  useEffect(() => {
    const buildQrImage = async () => {
      if (!setupData?.qrCodeUrl) {
        setQrImageUrl("");
        return;
      }

      setQrImageUrl("");
      const rawValue = setupData.qrCodeUrl;

      if (rawValue.startsWith("data:image") || rawValue.startsWith("http") || rawValue.startsWith("/")) {
        setQrImageUrl(rawValue);
        return;
      }

      try {
        if (rawValue.startsWith("otpauth://")) {
          const dataUrl = await QRCode.toDataURL(rawValue, { width: 200, margin: 1 });
          setQrImageUrl(dataUrl);
          return;
        }

        if (isLikelyBase64(rawValue)) {
          setQrImageUrl(`data:image/png;base64,${rawValue}`);
          return;
        }

        setQrImageUrl(rawValue);
      } catch (error) {
        showNotify("error", t("Failed to generate QR code"));
      }
    };

    buildQrImage();
  }, [setupData, showNotify, t]);

  const handleCopyKey = async () => {
    if (!setupData?.manualEntryKey) {
      return;
    }

    try {
      await navigator.clipboard.writeText(setupData.manualEntryKey);
      showNotify("success", t("Manual entry key copied"));
    } catch (error) {
      showNotify("error", t("Failed to copy manual entry key"));
    }
  };

  const handleVerify = async () => {
    setVerifyLoading(true);
    const result = await verify(verifyCode);

    if (!result.ok) {
      showNotify("error", getErrorMessage(result));
      setVerifyLoading(false);
      return;
    }

    showNotify("success", t(result.message || "Code is valid"));
    const enableResult = await enable(verifyCode);
    if (!enableResult.ok) {
      showNotify("error", getErrorMessage(enableResult));
      setVerifyLoading(false);
      return;
    }

    showNotify("success", t(enableResult.message || "MFA has been enabled"));
    setVerifyLoading(false);
    handleLoadStatus();
  };

  const handleDisable = async () => {
    setDisableLoading(true);
    const result = await disable(disableCode);

    if (!result.ok) {
      showNotify("error", getErrorMessage(result));
      setDisableLoading(false);
      return;
    }

    showNotify("success", t(result.message || "MFA has been disabled"));
    setDisableLoading(false);
    handleLoadStatus();
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={4}>
          <Notification notify={notify} setNotify={setNotify} />
          <Stack spacing={1}>
            <Stack direction="row" spacing={2} alignItems="center">
              <SecurityIcon />
              <Typography variant="h5">{t("MFA Settings")}</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t("Manage multi-factor authentication for your account")}
            </Typography>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="h6">{t("Status")}</Typography>
            {statusLoading && <CircularProgress size={24} />}
            {!statusLoading && !statusError && (
              <Typography color={status.isEnabled ? "success.main" : "error.main"} sx={{ fontWeight: 600 }}>
                {status.isEnabled ? t("MFA enabled") : t("MFA disabled")}
              </Typography>
            )}
          </Stack>

          {!status.isEnabled && (
            <>
              <Divider />
              <Stack spacing={2}>
                <Typography variant="h6">{t("Setup authenticator")}</Typography>
                <Button variant="contained" size="small" onClick={handleSetup} disabled={setupLoading}>
                  {setupLoading ? t("Loading...") : t("Setup authenticator")}
                </Button>
                {setupData && (
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={3}
                      alignItems="flex-start"
                      justifyContent="space-between"
                    >
                      <Stack spacing={2} sx={{ flex: 1 }}>
                        {setupData.qrCodeUrl && (
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              {t("Scan this QR code in your authenticator app")}
                            </Typography>
                            <Box>
                              {qrImageUrl && <img src={qrImageUrl} alt={t("MFA QR code")} width="200" />}
                            </Box>
                          </Stack>
                        )}
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t("Manual entry key")}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                              sx={{ width: { xs: "100%", sm: 320 } }}
                              value={setupData.manualEntryKey || ""}
                              InputProps={{
                                readOnly: true,
                              }}
                              aria-label={t("Manual entry key")}
                            />
                            <Tooltip title={t("Copy manual entry key")}>
                              <IconButton aria-label={t("Copy manual entry key")} onClick={handleCopyKey}>
                                <ContentCopyIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </Stack>
                      <Card variant="outlined" sx={{ width: { xs: "100%", md: 320 }, ml: { md: "auto" } }}>
                        <CardContent>
                          <Stack spacing={1}>
                            <Typography variant="subtitle1">
                              <strong>{t("Recommended authenticators")}</strong>
                            </Typography>
                            <List dense>
                              <ListItem disableGutters>
                                <ListItemText primary={`• ${t("Google Authenticator")}`} />
                              </ListItem>
                              <ListItem disableGutters>
                                <ListItemText primary={`• ${t("Microsoft Authenticator")}`} />
                              </ListItem>
                            </List>
                            <Typography variant="subtitle1">
                              <strong>{t("Setup steps")}</strong>
                            </Typography>
                            <List dense>
                              <ListItem disableGutters>
                                <ListItemText primary={`• ${t("Install one of the apps above")}`} />
                              </ListItem>
                              <ListItem disableGutters>
                                <ListItemText primary={`• ${t("Open the app and add a new account")}`} />
                              </ListItem>
                              <ListItem disableGutters>
                                <ListItemText primary={`• ${t("Scan the QR code or use the manual entry key")}`} />
                              </ListItem>
                              <ListItem disableGutters>
                                <ListItemText primary={`• ${t("Enter the 6 digit code to enable MFA")}`} />
                              </ListItem>
                            </List>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Stack>
                    <Stack spacing={2}>
                      <TextField
                        label={t("Code")}
                        value={verifyCode}
                        onChange={(event) => setVerifyCode(sanitizeCode(event.target.value))}
                        sx={{ width: { xs: "100%", sm: 240 } }}
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          maxLength: 6,
                          "aria-label": t("MFA code"),
                        }}
                        placeholder={t("Enter 6 digit code")}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ width: { xs: "100%", sm: 240 } }}
                        onClick={handleVerify}
                        disabled={verifyLoading || !verifyCode}
                      >
                        {verifyLoading ? t("Enabling...") : t("Verify and enable")}
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </>
          )}

          {status.isEnabled && (
            <>
              <Divider />
              <Stack spacing={2}>
                <Typography variant="h6">{t("Disable MFA")}</Typography>
                <TextField
                  label={t("Code")}
                  value={disableCode}
                  onChange={(event) => setDisableCode(sanitizeCode(event.target.value))}
                  sx={{ width: { xs: "100%", sm: 240 } }}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: 6,
                    "aria-label": t("MFA code"),
                  }}
                  placeholder={t("Enter 6 digit code")}
                />
                <Button
                  variant="contained"
                  size="small"
                  sx={{ width: { xs: "100%", sm: 240 } }}
                  onClick={handleDisable}
                  disabled={disableLoading || !disableCode}
                >
                  {disableLoading ? t("Disabling...") : t("Disable MFA")}
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default MfaSettings;
